import React, { useState } from "react";
import { useAction } from "wasp/client/operations";
import { createRetailStore } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Building2,
  Phone,
  Save,
  AlertCircle,
  Star,
  Clock,
  MapPin,
  Globe,
  Tag,
} from "lucide-react";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { useLocationMembership } from "../../client/hooks/useLocationMembership";
import { AudienceSelect, type Audience } from "../../client/components/AudienceSelect";
import { MembersOnlyNotice } from "../../client/components/MembersOnlyNotice";

interface RetailFormData {
  storeName: string;
  description: string;
  storeType: string;
  category: string;
  businessHours: string;
  contactInfo: string;
  services: string;
  unitNumber: string;
  floor: string;
  building: string;
  website: string;
  isVerified: boolean;
  isFeatured: boolean;
  visibility: Audience;
}

const RETAIL_CATEGORIES = [
  { value: "CLOTHING", label: "Clothing" },
  { value: "ELECTRONICS", label: "Electronics" },
  { value: "HOME_GARDEN", label: "Home & Garden" },
  { value: "SPORTS", label: "Sports" },
  { value: "BOOKS", label: "Books" },
  { value: "BEAUTY", label: "Beauty" },
  { value: "TOYS", label: "Toys" },
  { value: "AUTOMOTIVE", label: "Automotive" },
  { value: "FOOD_BEVERAGE", label: "Food & Beverage" },
  { value: "HEALTHCARE", label: "Healthcare" },
  { value: "PROFESSIONAL_SERVICES", label: "Professional Services" },
  { value: "OTHER", label: "Other" },
];

const STORE_TYPES = [
  { value: "RETAIL_STORE", label: "Retail Store" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "SERVICE_SHOP", label: "Service Shop" },
  { value: "BOUTIQUE", label: "Boutique" },
  { value: "DEPARTMENT_STORE", label: "Department Store" },
  { value: "SPECIALTY_STORE", label: "Specialty Store" },
  { value: "OTHER", label: "Other" },
];

export default function AddRetailPage() {
  const { isMember, isLoading: membershipLoading, locationCode } = useLocationMembership();
  useRequireSectionVisible("showRetail");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const createRetailStoreFn = useAction(createRetailStore);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RetailFormData>({
    storeName: "",
    description: "",
    storeType: "RETAIL_STORE",
    category: "OTHER",
    businessHours: "",
    contactInfo: "",
    services: "",
    unitNumber: "",
    floor: "",
    building: "",
    website: "",
    isVerified: false,
    isFeatured: false,
    visibility: "VERIFIED",
  });

  const handleChange = (field: keyof RetailFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.storeName.trim()) newErrors.storeName = "Store name is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = "Website must start with http:// or https://";
    }
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await createRetailStoreFn({
        storeName: formData.storeName.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        storeType: formData.storeType as any,
        category: formData.category as any,
        businessHours: formData.businessHours.trim(),
        contactInfo: formData.contactInfo.trim(),
        services: formData.services.split(",").map((s) => s.trim()).filter(Boolean),
        unitNumber: formData.unitNumber.trim(),
        floor: formData.floor.trim(),
        building: formData.building.trim(),
        website: formData.website.trim(),
        isVerified: formData.isVerified,
        isFeatured: formData.isFeatured,
        visibility: formData.visibility,
      });
      handleGoBack();
    } catch (error) {
      console.error("Error creating retail store:", error);
      setErrors({ submit: "Failed to create store. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/retail`);
    } else {
      navigate("/retail");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to add stores.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!membershipLoading && !isMember) {
    return (
      <IndividualEditPageLayout title="Add Retail Store" icon={Building2}>
        <MembersOnlyNotice locationCode={locationCode} />
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="retail-form"
        disabled={isSubmitting || !formData.storeName.trim()}
        icon={Save}
      >
        {isSubmitting ? "Creating..." : "Add Store"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack} disabled={isSubmitting}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Store Options" hideOnMobile>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-blue-200 transition-colors">
            <input
              type="checkbox"
              checked={formData.isVerified}
              onChange={(e) => handleChange("isVerified", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900">Verified Store</span>
              <span className="text-xs text-slate-500">Official business badge</span>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-blue-200 transition-colors">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => handleChange("isFeatured", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900">Featured</span>
              <span className="text-xs text-slate-500">Show on homepage</span>
            </div>
          </label>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Add Retail Store"
      subtitle={`Location: ${selectedLocation?.name || "Marketplace"}`}
      icon={Building2}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="retail-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Store Name</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.storeName}
                onChange={(e) => handleChange("storeName", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                placeholder="e.g. TechHub Electronics"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              placeholder="Tell us about your store..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Tag className="w-4 h-4 text-blue-600" /> Categorization
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                >
                  {RETAIL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Type</label>
                <select
                  value={formData.storeType}
                  onChange={(e) => handleChange("storeType", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                >
                  {STORE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Business Hours</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.businessHours}
                  onChange={(e) => handleChange("businessHours", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="e.g. Mon-Fri 9AM-5PM"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" /> Location Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Building</label>
                <input
                  type="text"
                  value={formData.building}
                  onChange={(e) => handleChange("building", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="e.g. Block A"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Unit/Floor</label>
                <input
                  type="text"
                  value={formData.unitNumber}
                  onChange={(e) => handleChange("unitNumber", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="e.g. #101"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Website</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-600" /> Contact Info
          </h3>
          <input
            type="text"
            value={formData.contactInfo}
            onChange={(e) => handleChange("contactInfo", e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
            placeholder="How should customers contact you?"
          />
        </div>

        <AudienceSelect
          value={formData.visibility}
          onChange={(value) => handleChange("visibility", value)}
        />

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
